/**
 * **键盘输入** —— 挂在 DOM 上的那一半。纯逻辑在 `ui/keys.ts`。
 *
 * 这个文件只做四件事，一件也不多做：
 *
 * 1. 把 `keydown` 归一化成 `KeyEvent`（单字符转小写）
 * 2. **丢掉输入法正在组字的按键**（`isComposing` / `Process` / `Unidentified`）
 * 3. 有可编辑元素获得焦点时不接管（将来真要加输入框也不会打架）
 * 4. 把带 Ctrl / Cmd 的组合键留给浏览器（复制、刷新、开发者工具）
 *
 * ⚠️ 关于"输入法怎么会进来"：本项目**不放任何 DOM 输入控件**，
 * 所以正常情况下输入法根本不会被激活。第 2 条是**防御性**的 ——
 * 万一某个平台/扩展仍然把输入法接了进来，也不会把候选字当成名字吃进去。
 * 完整的来龙去脉见 `ui/keys.ts` 的文件头。
 */

import type { KeyEvent } from '../ui/keys';

export interface KeyboardHandle {
  destroy(): void;
}

/** 这个元素是不是"可编辑"的 —— 可编辑时我们不接管按键 */
function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * 绑定全局键盘。返回的句柄负责解绑 ——
 * 调试页反复热更新时不解绑就会越挂越多，一次按键跑好几遍。
 */
export function bindKeyboard(onKey: (event: KeyEvent) => void): KeyboardHandle {
  const handler = (raw: KeyboardEvent): void => {
    // 输入法正在组字 —— 直接丢掉，别把候选字吃进名字里
    if (raw.isComposing) return;
    if (raw.key === 'Process' || raw.key === 'Unidentified') return;
    if (isEditable(raw.target)) return;

    // 带 Ctrl / Cmd 的组合键留给浏览器
    if (raw.ctrlKey || raw.metaKey || raw.altKey) return;

    onKey({
      // ⚠️ **不要在这里转小写。** 原来这一行是 `raw.key.toLowerCase()`，
      //    理由是"让 TextBuffer 不用关心大小写"—— 那是错的：大小写是**字段**的事，
      //    不是键盘层的事。转小写的后果是**昵称永远打不出大写字母**
      //    （玩家名"Pilot"会变成"pilot"），而房间号要的是大写、
      //    它靠自己的 `transform` 解决。见 `ui/keys.ts` 的说明。
      key: raw.key,
      shift: raw.shiftKey,
      raw,
    });
  };

  window.addEventListener('keydown', handler);
  return {
    destroy() {
      window.removeEventListener('keydown', handler);
    },
  };
}
